import { useParams, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { PageWrapper } from '../components/layout/PageWrapper';
import { AuthGuard } from '../components/auth/AuthGuard';
import { Seo } from '../components/Seo';
import { GameBoard } from '../components/game/GameBoard';
import { LevelLobby } from '../components/game/LevelLobby';
import { usePublishedLevels } from '../hooks/useGameLevels';
import { LEVELS as STATIC_LEVELS } from '../data/levels';
import type { GameLevel } from '../types/game';

interface GamePageProps {
  onLoginRequired: () => void;
}

export function GamePage({ onLoginRequired }: GamePageProps) {
  const { t } = useTranslation('game');
  const { levelId } = useParams<{ levelId?: string }>();
  const { data: publishedLevels, isLoading, isError } = usePublishedLevels();

  // Resolve level: API published → static fallback
  let level: GameLevel | null = null;
  let walkthrough: string | undefined;
  if (levelId) {
    const storedLevel = publishedLevels?.find((s) => s.levelId === levelId);
    const fromApi = storedLevel?.levelData ?? null;
    const fromStatic = STATIC_LEVELS.find((l) => l.id === levelId) ?? null;
    level = fromApi ?? fromStatic;
    walkthrough = storedLevel?.walkthrough;
  }

  return (
    <>
      <Seo
        title="Игра — детективная головоломка"
        description="Логическая игра-головоломка на базе спортивной мафии. Определи роли всех игроков."
        path={levelId ? `/game/${levelId}` : '/game'}
      />
      <AuthGuard onLoginRequired={onLoginRequired}>
        {levelId ? (
          isLoading ? (
            <div className="flex items-center justify-center py-20">
              <div className="w-6 h-6 border-2 border-mafia-gold/40 border-t-mafia-gold rounded-full animate-spin" />
            </div>
          ) : !level ? (
            <PageWrapper>
              <div className="text-center py-12">
                <p className="text-mafia-muted mb-4">Уровень не найден</p>
                <Link to="/game" className="btn-gold text-sm">← К делам</Link>
              </div>
            </PageWrapper>
          ) : (
            <PageWrapper className="!py-6 !max-w-none !px-4">
              <div className="mb-4">
                <Link to="/game" className="text-xs font-mono tracking-widest uppercase text-mafia-paper-dim hover:text-mafia-gold transition-colors">
                  {t('board.backToLobby')}
                </Link>
              </div>
              <GameBoard level={level} walkthrough={walkthrough} />
            </PageWrapper>
          )
        ) : (
          <PageWrapper>
            <LevelLobby
              levels={publishedLevels ?? []}
              loading={isLoading}
              error={isError}
            />
          </PageWrapper>
        )}
      </AuthGuard>
    </>
  );
}
